/**
 * Where the attachment sets live: the one module that names the routes, the byte URLs the screens
 * hand to the shared viewer loaders, the viewer file ids that carry a set page's address, and
 * which page a thumbnail is. No transport here: the markup client reads the file ids without
 * loading requests.
 */
/** The proxy prefix of the server's routes. */
const API_PROXY_PREFIX = "/api/ai";
export const ATTACHMENT_SETS_ROUTE = `${API_PROXY_PREFIX}/document-workflows/attachment-sets`;
const base = ATTACHMENT_SETS_ROUTE;

/** A set's own route. */
export const setRoute = (setId: string) => `${base}/${setId}`;

/** An item's section and its one set (`records/{id}`). */
export const recordRoute = (recordId: string) => `${base}/records/${recordId}`;

/** The sent items an item may copy pages from (`records/{id}/copy-sources`). */
export const copySourcesRoute = (recordId: string) => `${recordRoute(recordId)}/copy-sources`;

/** The pins of the sheets an item's References name (`records/{id}/reference-pins`). */
export const referencePinsRoute = (recordId: string) => `${recordRoute(recordId)}/reference-pins`;

/** The items and sets a sheet revision's pages are in (`sheet-uses/{section_group_id}`). */
export const sheetUsesRoute = (sectionGroupId: string) => `${base}/sheet-uses/${sectionGroupId}`;

/** The details a sheet revision offers (`sheets/{section_group_id}/details`). */
export const sheetDetailsRoute = (sectionGroupId: string) =>
    `${base}/sheets/${sectionGroupId}/details`;

/** The detail a reference names ("4/M5.102"): `detail-search?ref=`. */
export const DETAIL_SEARCH_ROUTE = `${base}/detail-search`;

/** A set's own routes below `setRoute`: its pages and their commands, its document, its revisions. */
export const setRoutes = {
    pages: (setId: string) => `${setRoute(setId)}/pages`,
    copyPages: (setId: string) => `${setRoute(setId)}/pages/copy`,
    movePages: (setId: string) => `${setRoute(setId)}/pages/move`,
    rotatePages: (setId: string) => `${setRoute(setId)}/pages/rotate`,
    deletePages: (setId: string) => `${setRoute(setId)}/pages/delete`,
    extractPages: (setId: string) => `${setRoute(setId)}/pages/extract`,
    page: (setId: string, pageId: string) => pageRoute({ kind: "page", setId, pageId }),
    replacePage: (setId: string, pageId: string) =>
        `${pageRoute({ kind: "page", setId, pageId })}/replace`,
    referencesSync: (setId: string) => `${setRoute(setId)}/references-sync`,
    document: (setId: string) => `${setRoute(setId)}/document`,
    revisions: (setId: string) => `${setRoute(setId)}/revisions`,
    revision: (setId: string, rev: number) => `${setRoute(setId)}/revisions/${rev}`,
    retryRevision: (setId: string, rev: number) => `${setRoute(setId)}/revisions/${rev}/retry`,
};

/** The one command that adds a plain file to a record: records level, outside the attachment sets. */
export const recordFilesRoute = (recordId: string) =>
    `${API_PROXY_PREFIX}/document-workflows/${recordId}/files`;

/** A page of a set: a working page, or the page at `position` of a sent revision (read only). */
export type SetPageAddress =
    | { kind: "page"; setId: string; pageId: string }
    | { kind: "revision"; setId: string; rev: number; position: number };

/**
 * A working page's markup layer, as a viewer file id names it: the page and the `geometry` it is
 * drawn on (the host's page map), which a save names so a page changed since is refused.
 */
export interface SetLayerAddress {
    kind: "page";
    setId: string;
    pageId: string;
    geometry: number;
}

/** Where a page's own routes live (its markup, its thumbnail). */
export const pageRoute = (address: SetPageAddress) =>
    address.kind === "page"
        ? `${base}/${address.setId}/pages/${address.pageId}`
        : `${base}/${address.setId}/revisions/${address.rev}/pages/${address.position}`;

/** A page's markup layer (read and save). */
export const pageMarkupRoute = (address: SetPageAddress) => `${pageRoute(address)}/markup`;

/**
 * The picture of the image snapshot `shapeId` on the page's layer (a PNG of the region it shows,
 * as this reader may read it; a sent revision's from the bytes it was sent with).
 */
export const snapshotPictureRoute = (address: SetPageAddress, shapeId: string) =>
    `${pageRoute(address)}/snapshots/${encodeURIComponent(shapeId)}.png`;

/**
 * The byte routes a screen hands to the shared authenticated loaders. All are authenticated GETs
 * that answer HEAD with Content-Length.
 */
export const attachmentSetUrls = {
    source: (setId: string, pageId: string) => `${base}/${setId}/pages/${pageId}/source.pdf`,
    /** A draft's Download PDF: the working copy built as the file Send makes, handed out to save. */
    download: (setId: string) => `${base}/${setId}/download.pdf`,
    revisionPdf: (setId: string, rev: number) => `${base}/${setId}/revisions/${rev}/pdf`,
    revisionPageSource: (setId: string, rev: number, position: number) =>
        `${base}/${setId}/revisions/${rev}/pages/${position}/source.pdf`,
};

/**
 * A page's thumbnail, the layer drawn in, asked for at the `thumbnail_version` its picture shows
 * (`?v=`): the server answers that version's picture as one the browser keeps for good. Not a
 * public address: thumbnails are read by `useSetThumbnail`, with the session's credentials, never
 * by a bare image request.
 */
export const thumbnailUrl = (address: SetPageAddress, version: string) =>
    `${pageRoute(address)}/thumbnail.png?v=${encodeURIComponent(version)}`;

/**
 * An address the server wrote (`pdf.url`) is in the server's own space, without the proxy prefix;
 * this is the one place that puts it under `/api/ai`. An address that already carries it is kept.
 */
export const throughProxy = (serverAddress: string) =>
    serverAddress.startsWith(`${API_PROXY_PREFIX}/`)
        ? serverAddress
        : `${API_PROXY_PREFIX}${serverAddress}`;

/**
 * The viewer file id of a working page's markup layer, with the geometry it is drawn on (required:
 * the layer's save fence). `api/markup.ts` maps it to the markup route; a page turned, cropped or
 * replaced is another id, so the viewer reads that page's layer afresh.
 */
export const setPageFileId = (setId: string, pageId: string, geometry: number) =>
    `attachment-page:${setId}:${pageId}:${geometry}`;
/** The viewer file id of a set's working document, one per content: new bytes are a new file. */
export const workingDocumentFileId = (setId: string, contentHash: string) =>
    `attachment-document:${setId}:${contentHash}`;

/** Parses a viewer file id back to a set page's layer, or null when it is any other file. */
export function parseSetFileId(fileId: string): SetLayerAddress | null {
    const page = /^attachment-page:([^:]+):([^:]+):(\d+)$/.exec(fileId);
    return page
        ? { kind: "page", setId: page[1], pageId: page[2], geometry: Number(page[3]) }
        : null;
}
